import React, {memo, useCallback, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {ColumnModel, SettingsList} from '../../../../components/Settings/SettingsList';
import {useParams} from 'react-router-dom';
import {AreaModel} from '../../../../store/control/state';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useLocalization} from "../../../../hooks/localization";
import {ICON_TYPES} from '../AreaListScreen';

const keyExtractor = (item: AreaModel) => item.id;

export const RoomListScreen = memo(function RoomListScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string; }>();
    const areaSet = useSetSelector(state => state.control.areaList);
    const roomList = useFilterSelector(state => state.control.areaList, room => room.type === 'room' && room.parent_area_id === params.id, [params.id]);

    const [selected, setSelected] = useState<AreaModel>(null);
    const selectedFloor = useMemo(() => {
        if (params.id == null) {
            return null;
        }

        const floor = areaSet[params['id']];
        if (floor == null) {
            return null;
        }

        return floor;
    }, [areaSet, params]);

    const buttons = useMemo(() => [
        {
            text: t('edit'),
            url: `/settings/floor/${selectedFloor.id}/room/edit/${selected ? selected.id : ''}`,
            className: 'orange',
            hidden: !(selected),
        },
        {
            text: t('create_room'),
            url: `/settings/floor/${selectedFloor.id}/room/edit`,
            className: 'green',
        },
    ], [t, selected, selectedFloor]);

    const displayNameExtractor = useCallback((item: AreaModel, column: ColumnModel) => {
        if (column.key === 'floorName') {
            return selectedFloor != null ? selectedFloor.name : '';
        }
        if (column.key === 'icon_type') {
            return t(ICON_TYPES[item.icon_type]) || '-';
        }

        return item[column.key];
    }, [selectedFloor, t]);

    const columns = useMemo<ColumnModel[]>(() => [
        {
            length: 3,
            name: t('room_name'),
            key: 'name',
        },
        {
            length: 3,
            name: t('floor_name'),
            key: 'floorName',
        },
        {
            length: 3,
            name: t('icon'),
            key: 'icon_type',
        },
    ], [t]);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer buttons={buttons} backUrl='/settings/floors-and-rooms'>
                <SettingsList
                    columns={columns}
                    items={roomList}
                    displayNameExtractor={displayNameExtractor}
                    keyExtractor={keyExtractor}
                    selectedItem={selected}
                    onItemSelected={setSelected}/>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
